import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKey } from '../../utils/api';
import { isClient } from '../../utils/utils';
import { bffPut } from '../bff';
import { BffQueryKey } from '../model/BffQueryKey';
import { useBasket } from './useBasket';
import type { PageSource } from '../../types/page-source';
import type { HttpParams } from '../../utils/url-utils';
import type { OrderAddress } from './model';
import type { BasketAvailability } from './model/BasketAvailability';

interface UpdateShippingAddressProps {
    address: OrderAddress;
    useAsBilling?: boolean;
}

export const useUpdateShippingAddress = (
    pageSource?: PageSource,
): UseMutationResult<BasketAvailability, unknown, UpdateShippingAddressProps> => {
    const { data } = useBasket();
    const authContext = useAuthContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ address, useAsBilling = false }: UpdateShippingAddressProps) => {
            if (isClient) await window.sfccSessionCreationPromise;
            const authData = await authContext.ready();

            const httpParams: HttpParams = [{ useAsBilling }];

            if (pageSource) {
                httpParams.push({ pageSource });
            }

            return bffPut(`/baskets/${data?.basketId}/shipping-address`, {
                basketCustomerId: data?.customerInfo?.customerId,
                accessToken: authData.access_token,
                customerId: authData.customer_id,
                body: address,
                httpParams,
            });
        },
        onSuccess: async (availability): Promise<void> => {
            queryClient.setQueryData(
                [
                    BffQueryKey.BASKET_AVAILABILITY,
                    data?.basketId ?? '',
                    data?.c_bopisStoreID ?? '',
                    data?.c_goLocalStoreID ?? '',
                ],
                availability,
            );

            await Promise.all([
                queryClient.invalidateQueries([BffQueryKey.BASKET]),
                queryClient.invalidateQueries([BffQueryKey.SHIPPING_METHODS], {
                    exact: false,
                }),
                queryClient.invalidateQueries([BffQueryKey.CUSTOMER]),
                queryClient.invalidateQueries([QueryKey.USER_ADDRESS_ZIP_CODE], {
                    exact: false,
                }),
            ]);
        },
    });
};
